import { Button, Chip, tokens } from "@blurb/codex-react";
import {
  isBetweenSearch,
  isContainsSearch,
  isEqualsSearch,
  isInSearch,
  SearchInput,
} from "../configuration/filters/searchInput";
import { createStyles } from "@mantine/core";
import { Body, Label } from "./Typography";

const useFilterChipStyles = createStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
  },
  filterItems: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacing[2],
  },
  clearAllSection: {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    gap: tokens.spacing[3],
  },
  divider: {
    alignSelf: "center",
    width: 1,
    height: "1rem",
    background: tokens.color.semantic.icon.inverseSubtleHover,
  },
  clearButton: {
    padding: 0,
  },
});

interface FilterChipsProps<T extends string> {
  filters: SearchInput<T>[];
  onSetFilters: (value: (prevFilters: SearchInput<T>[]) => SearchInput<T>[]) => void;
  filterLabels?: Partial<Record<T, string>>;
  valueRenderers?: Partial<Record<T, (value: string) => string>>;
}

export function FilterChips<T extends string>({
  filters,
  onSetFilters,
  filterLabels,
  valueRenderers,
}: FilterChipsProps<T>) {
  const { classes } = useFilterChipStyles();
  return (
    <div className={classes.container}>
      <div className={classes.filterItems}>
        {filters.map((filter, index) => (
          <FilterChip
            key={`${filter.field}-${index}`}
            filter={filter}
            label={filterLabels?.[filter.field]}
            valueRender={valueRenderers?.[filter.field]}
            onRemoveFilter={() =>
              onSetFilters((prevFilters) => {
                return prevFilters.filter((value) => value !== filter);
              })
            }
          />
        ))}
      </div>
      {filters.length > 0 && (
        <div className={classes.clearAllSection}>
          <Body>
            {filters.length} filter{filters.length > 1 ? "s" : ""} applied
          </Body>
          <div className={classes.divider} />
          <Button
            variant="text"
            intent="primary"
            className={classes.clearButton}
            onClick={() => onSetFilters(() => [])}
          >
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
}

interface FilterChipProps<T extends string> {
  filter: SearchInput<T>;
  onRemoveFilter: () => void;
  label?: string;
  valueRender?: (value: string) => string;
}

function FilterChip<T extends string>({ filter, onRemoveFilter, label, valueRender }: FilterChipProps<T>) {
  const chipValue = getChipValue(filter, valueRender ?? defaultValueRender);
  if (!chipValue) {
    return null;
  }

  return (
    <Chip
      size="large"
      label={
        <span>
          <Label as="span">{label ?? filter.field}:</Label> <Body as="span">{chipValue}</Body>
        </span>
      }
      onDelete={onRemoveFilter}
    />
  );
}

function getChipValue<T extends string>(filter: SearchInput<T>, valueRender: (value: string) => string): string | null {
  if (isEqualsSearch(filter)) {
    return valueRender(filter.firstValue);
  } else if (isBetweenSearch(filter)) {
    return `${valueRender(filter.firstValue)}-${valueRender(filter.secondValue)}`;
  } else if (isInSearch(filter)) {
    if (filter.values.length === 1) {
      return valueRender(filter.values[0]);
    } else {
      return `${valueRender(filter.values[0])} +${filter.values.length - 1}`;
    }
  } else if (isContainsSearch(filter)) {
    return valueRender(filter.firstValue);
  } else {
    // if it didn't pass type narrowing, it's not a valid filter and we won't render it
    return null;
  }
}

function defaultValueRender(value: string) {
  return value;
}
